import React, { ReactElement } from 'react'

import { clsx } from 'clsx'
import { useAtomValue } from 'jotai'

import { ButtonClick, track } from '@dfds-frontend/tracking'

import { selectedDeparturesAtom, selectedRoutesAtom } from '@/states/selectionAtom'
import ArrowForwardIcon from '@/ui-cvi/components/dxp-icons/system/ArrowForwardIcon'

import { getArrivalRouteTitle, getDepartureRouteTitle } from '@/utils'

import { useTranslation } from '@/hooks/useDebugTranslations'

interface Props {
  onRouteFilterChange: (filters: RouteFilter) => void
  returnLeg?: boolean
  selectedRouteFilters: RouteFilter
}

export const getOtherPortCode = (code: string | null) => {
  if (code && code.includes('CA')) return 'frdkk'
  if (code && code.includes('DK')) return 'frcqf'
}

const DepartureRouteFilters: React.FC<Props> = ({ onRouteFilterChange, returnLeg, selectedRouteFilters }): ReactElement => {
  const selectedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedDepartures = useAtomValue(selectedDeparturesAtom)
  const isDepartureCompoundRoute = returnLeg
    ? !selectedRoutes.homebound?.substring(0, 2).includes('DV')
    : !selectedRoutes.outbound.substring(0, 2).includes('DV')
  const portCode = getOtherPortCode(
    isDepartureCompoundRoute ? selectedRoutes.outbound : returnLeg ? selectedRoutes.homebound : selectedRoutes.outbound
  )
  const { t } = useTranslation()

  return (
    <div className="w-full">
      {portCode ? (
        <button
          className={clsx(
            'focus-visible:outline-semantic-global-primary-default typography-body-md-medium px-semantic-global-md py-semantic-global-xs flex w-full cursor-pointer items-center justify-center whitespace-nowrap select-none focus-visible:outline-2 focus-visible:outline-offset-2',
            selectedRouteFilters[portCode]
              ? 'bg-semantic-global-background-light-sky-blue text-semantic-global-primary-default border-semantic-global-primary-default border'
              : 'bg-semantic-global-background-white border-semantic-global-border-default text-semantic-global-text-default border transition-colors duration-150'
          )}
          data-e2e="route-filter"
          data-e2e-selected={selectedRouteFilters[portCode]}
          onClick={() => {
            const nextValue = !selectedRouteFilters[portCode]

            track<ButtonClick>({
              clickedElementText: `${nextValue ? 'including' : 'excluding'} ${t(portCode.toUpperCase())}`.toLowerCase(),
              event: 'buttonClick',
              eventAction: `FilterRoute${returnLeg ? 'Return' : 'Outward'}Clicked`,
            })

            if (portCode === 'frdkk') {
              onRouteFilterChange({ frcqf: true, frdkk: nextValue })
            }
            if (portCode === 'frcqf') {
              onRouteFilterChange({ frcqf: nextValue, frdkk: true })
            }
          }}
          tabIndex={0}
          type="button"
        >
          {!selectedRouteFilters[portCode] ? (
            <span className="mr-semantic-global-xs">{t('INCLUDE')}</span>
          ) : (
            <span className="mr-semantic-global-xs">{t('INCLUDING')}</span>
          )}
          {isDepartureCompoundRoute ? (
            <span className="flex items-center">
              {t(portCode.toUpperCase())} <ArrowForwardIcon className="mx-1" size="md" />
              {getArrivalRouteTitle(returnLeg ? true : false, selectedRouteFilters, selectedRoutes, selectedDepartures, t)}
            </span>
          ) : (
            <span className="flex items-center">
              {getDepartureRouteTitle(returnLeg ? true : false, selectedRouteFilters, selectedRoutes, selectedDepartures, t)}{' '}
              <ArrowForwardIcon className="mx-1" size="md" /> {t(portCode.toUpperCase())}
            </span>
          )}
        </button>
      ) : null}
    </div>
  )
}

export default DepartureRouteFilters

export interface RouteFilter {
  frcqf: boolean
  frdkk: boolean
}
